import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { FileText, Shield, Scale, Mail, Phone, MapPin } from 'lucide-react';

const TermsAndConditions: React.FC = () => {
  const sections = [
    {
      id: "use-of-website",
      title: "1. Use of Website",
      content: [
        "The content on this website is provided by the Company for general informational purposes only.",
        "Users agree not to misuse the website, attempt to gain unauthorized access, or engage in any activity that could harm, disable, or impair the website.",
        "Users may not use this website for unlawful purposes or in a manner that violates applicable laws and regulations."
      ]
    },
    {
      id: "intellectual-property",
      title: "2. Intellectual Property",
      content: [
        "All content on this website, including but not limited to text, graphics, logos, designs, icons, images, software, and trademarks, are the intellectual property of the Company unless otherwise stated.",
        "No part of this website may be reproduced, distributed, modified, or exploited without the prior written consent of the Company."
      ]
    },
    {
      id: "services-disclaimer",
      title: "3. Services Disclaimer",
      content: [
        "The Company provides consulting and IT solutions related to Dynamics 365, Power Platform, CRM customizations, and related services.",
        "While the Company strives to ensure the accuracy, completeness, and reliability of the information provided, no warranties or representations are made regarding the suitability, reliability, or availability of the services, information, or related graphics contained on this website.",
        "Any reliance placed on such information is strictly at the user's own risk."
      ]
    },
    {
      id: "third-party-links",
      title: "4. Third-Party Links",
      content: [
        "This website may contain links to external websites provided for convenience.",
        "The Company does not control or endorse the content, products, or services offered by third-party websites and shall not be responsible for any damages or losses arising from their use."
      ]
    },
    {
      id: "limitation-of-liability",
      title: "5. Limitation of Liability",
      content: [
        "To the maximum extent permitted by law, the Company shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages resulting from the use of this website or the services provided.",
        "This includes, without limitation, damages for loss of data, profits, or business interruption."
      ]
    },
    {
      id: "privacy-policy",
      title: "6. Privacy Policy",
      content: [
        "The use of this website is also governed by the Company's Privacy Policy, which explains how personal data is collected, used, and protected.",
        "By using this website, users consent to the practices described in the Privacy Policy."
      ]
    },
    {
      id: "modifications-to-terms",
      title: "7. Modifications to Terms",
      content: [
        "The Company reserves the right to update or modify these Terms and Conditions at any time without prior notice.",
        "Continued use of the website following modifications constitutes acceptance of the revised Terms."
      ]
    },
    {
      id: "governing-law",
      title: "8. Governing Law",
      content: [
        "These Terms and Conditions shall be governed by and construed in accordance with the laws of India.",
        "Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Andhra Pradesh, India."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <SEOHead 
        title="Terms and Conditions - Website Usage Terms & Legal Agreement"
        description="CRMONCE Terms and Conditions outline the rules and regulations for using our website and services. Read our legal agreement and usage guidelines."
        keywords="terms and conditions, legal agreement, website terms, usage policy, service agreement, legal terms"
        canonical="/terms-and-conditions"
      />
      
      {/* Hero Section */}
      <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FileText className="w-12 h-12 mr-4" />
              <h1 className="text-5xl font-bold">Terms and Conditions</h1>
            </div>
            <p className="text-xl text-blue-100 mb-4">
              Last Updated: September 18, 2025
            </p>
            <p className="text-lg text-blue-100">
              Welcome to crmonce.com, the official website of CRMONCE (OPC) Pvt Ltd. By accessing or using this website, 
              you agree to comply with and be bound by the following Terms and Conditions. If you do not agree, you should not use this website.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20">
        <div className="container-custom max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8 lg:p-12 mb-8"
          >
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Agreement to Terms</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of this website constitutes acceptance of these terms.
            </p>
          </motion.div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {section.title}
                </h3>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg p-8 mt-12"
          >
            <div className="flex items-center mb-6">
              <Scale className="w-6 h-6 mr-3" />
              <h3 className="text-2xl font-bold">9. Contact Information</h3>
            </div>
            <p className="text-blue-100 mb-6">
              For any questions or concerns regarding these Terms and Conditions, please contact the Company at:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">CRMONCE (OPC) Pvt Ltd</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 mr-3 text-blue-200" />
                    <a href="mailto:info@crmonce.com" className="text-blue-100 hover:text-white transition-colors">
                      info@crmonce.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 mr-3 text-blue-200" />
                    <a href="tel:+918096556344" className="text-blue-100 hover:text-white transition-colors">
                      +91 80965 56344
                    </a>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-3">Address</h4>
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mr-3 text-blue-200 mt-1 flex-shrink-0" />
                  <div className="text-blue-100">
                    <p>8-112, Gamallapalem, Kodurupadu</p>
                    <p>Nellore, AP-524314, India</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Legal Notice */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="bg-gray-50 rounded-xl p-6 mt-8 text-center"
          >
            <p className="text-sm text-gray-600">
              These Terms and Conditions constitute the entire agreement between you and CRMONCE (OPC) Pvt Ltd 
              regarding your use of this website. If any provision is found to be unenforceable, 
              the remaining provisions will continue in full force and effect.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;
